import { t } from 'i18next';
import { isEmpty, isString, toLower, upperFirst } from 'lodash';
import { Dispatch, SetStateAction } from 'react';

import { PartAliasCell } from 'bom/elements';
import { BomPartCellHelper } from 'bom/helpers';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { StatusCell } from 'core/elements';
import { AgGridFilterOption } from 'core/enums';
import { getTranslationKey } from 'core/utils';
import { PartDetailsTab } from 'parts/components/PartDetails';
import { ImageCell } from 'parts/elements';
import {
  getLineTypeCellValue,
  getManufacturerCellValue,
  QuotePartColId,
  QuotePartInlineActionDrawer,
} from 'quotes/components/QuotePartList';
import {
  DatasheetCell,
  DescriptionCell,
  InventoryCell,
  InventoryStatusCell,
  LineTypeCell,
  PackagingCell,
  TaxonomyCell,
} from 'quotes/elements';
import { QuotePartStatus } from 'quotes/enums';
import { IQuotePart } from 'quotes/types';
import { TaxonomyHelper } from 'taxonomy/helpers';
import { ITaxonomyPath } from 'taxonomy/types';
import { Color } from 'theme/enums';

export const commonColumns = (
  hasTaxonomyColumn: boolean,
  openInlineActionDrawer?: (data: IQuotePart, action: QuotePartInlineActionDrawer) => void,
  setDefaultQuotePartDetailsTab?: Dispatch<SetStateAction<PartDetailsTab>>,
  isDescriptionEditable?: ({ data }) => boolean
) => {
  const taxonomyColumn = hasTaxonomyColumn
    ? {
        colId: QuotePartColId.TAXONOMY,
        headerName: 'Taxonomy',
        field: 'taxonomyPath',
        suppressPaste: true,
        suppressFillHandle: true,
        minWidth: 150,
        maxWidth: 300,
        enableRowGroup: true,
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            {
              filter: 'agTextColumnFilter',
              filterParams: {
                textFormatter: (value: ITaxonomyPath[] | string) => {
                  if (isString(value)) {
                    return toLower(value);
                  }

                  return TaxonomyHelper.getTaxonomyPathNames(value as ITaxonomyPath[], true).join(
                    ' '
                  );
                },
                buttons: ['reset', 'apply'],
                closeOnApply: true,
                closeOnReset: true,
              },
            },
            {
              filter: 'agSetColumnFilter',
              filterParams: {
                valueFormatter: ({ value }) => value && value.name,
                keyCreator: ({ value }) => value && value.id,
                buttons: ['reset', 'apply'],
                closeOnApply: true,
                closeOnReset: true,
              },
            },
          ],
        },
        valueGetter: ({ data }) =>
          data && TaxonomyHelper.getLastLevelTaxonomy(data.taxonomyPath)?.name,
        filterValueGetter: ({ data }) =>
          data &&
          data.taxonomyPath &&
          data.taxonomyPath.filter((path: ITaxonomyPath) => path.level > 0),
        cellRenderer: TaxonomyCell,
      }
    : null;

  return {
    imageColumn: {
      colId: QuotePartColId.IMAGE,
      headerName: t('headerImage'),
      field: 'partArr.defaultImage',
      sortable: false,
      resizable: false,
      suppressSizeToFit: false,
      suppressHeaderMenuButton: true,
      filter: false,
      suppressPaste: true,
      suppressFillHandle: true,
      pinned: 'left',
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall',
      maxWidth: 74,
      valueFormatter: ({ value }) => value?.alt,
      cellRenderer: ImageCell,
    },
    manufacturerColumn: {
      colId: QuotePartColId.MANUFACTURER,
      headerName: t('headerManufacturer'),
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 120,
      width: 160,
      maxWidth: 190,
      valueGetter: ({ data }) => getManufacturerCellValue(data),
      cellRendererParams: {
        textEllipsis: false,
      },
    },
    descriptionColumn: {
      colId: QuotePartColId.DESCRIPTION,
      headerName: t('headerDescription'),
      autoHeight: true,
      field: 'description',
      filter: 'agTextColumnFilter',
      minWidth: 200,
      width: 355,
      maxWidth: 900,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      valueGetter: ({ data }) => BomPartCellHelper.getDescription(data),
      editable: isDescriptionEditable,
      cellRenderer: DescriptionCell,
    },
    locationInventoryColumn: {
      colId: QuotePartColId.LOCATION_INVENTORY,
      headerName: t('headerLocationInventory'),
      cellStyle: { alignItems: 'flex-end' },
      field: 'corporateLocation',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      filterValueGetter: ({ data }) => data?.corporateLocation?.inventoryTotalQty || 0,
      valueFormatter: ({ value }) => value?.inventoryTotalQty || 0,
      enableValue: true,
      enablePivot: true,
      onCellDoubleClicked: ({ data }) =>
        openInlineActionDrawer(data, QuotePartInlineActionDrawer.DETAILS),
      cellRenderer: InventoryCell,
      cellRendererParams: {
        quantityField: 'inventoryTotalQty',
        uomField: 'inventoryUom',
        onClick: (quotePart: IQuotePart) => {
          setDefaultQuotePartDetailsTab(PartDetailsTab.INVENTORY);
          openInlineActionDrawer(quotePart, QuotePartInlineActionDrawer.DETAILS);
        },
      },
    },
    allInventoryColumn: {
      colId: QuotePartColId.ALL_INVENTORY,
      headerName: t('headerAllInventory'),
      cellStyle: { alignItems: 'flex-end' },
      field: 'inventoryStatus',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      filterValueGetter: ({ data }) => data?.inventoryStatus?.totalQty,
      valueFormatter: ({ value }) => value?.totalQty,
      enableValue: true,
      enablePivot: true,
      onCellDoubleClicked: ({ data }) =>
        openInlineActionDrawer(data, QuotePartInlineActionDrawer.DETAILS),
      cellRenderer: InventoryCell,
      cellRendererParams: {
        quantityField: 'totalQty',
        uomField: 'uom',
        onClick: (quotePart: IQuotePart) => {
          setDefaultQuotePartDetailsTab(PartDetailsTab.INVENTORY);
          openInlineActionDrawer(quotePart, QuotePartInlineActionDrawer.DETAILS);
        },
      },
    },
    inventoryStatusColumn: {
      colId: QuotePartColId.INVENTORY_STATUS,
      headerName: t('headerInventoryStatus'),
      field: 'inventoryStatus.status',
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      filter: 'agSetColumnFilter',
      valueGetter: ({ data }) =>
        data?.inventoryStatus?.status &&
        t(getTranslationKey('labelInventoryStatus', data.inventoryStatus.status)),
      suppressPaste: true,
      suppressFillHandle: true,
      sortable: false,
      cellRenderer: InventoryStatusCell,
    },
    taxonomyColumn,
    packagingColumn: {
      colId: QuotePartColId.PACKAGING,
      headerName: t('headerPackaging'),
      field: 'packageUnits',
      filter: 'agNumberColumnFilter',
      sortable: false,
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 120,
      maxWidth: 150,
      cellRenderer: PackagingCell,
    },
    alternateColumn: {
      colId: QuotePartColId.ALTERNATE,
      headerName: t('headerAlternate'),
      field: 'masterQuotePartId',
      filter: 'agSetColumnFilter',
      enableValue: true,
      enablePivot: true,
      valueGetter: ({ data }) =>
        data?.masterQuotePartId > 0 ? t('labelAlternate') : DEFAULT_PLACEHOLDER,
      suppressPaste: true,
      suppressFillHandle: true,
      resizable: false,
    },
    alternateVendorPartNumberColumn: {
      colId: QuotePartColId.ALTERNATE_VENDOR_PART_NUMBER,
      headerName: t('labelAlternateVendorPartNumber'),
      field: 'alternateVendorPartNumber',
      filter: 'agTextColumnFilter',
    },
    customerPartNumberColumn: {
      colId: QuotePartColId.CUSTOMER_PART_NUMBER,
      headerName: t('headerCustomerPartNumber'),
      field: 'customerPartNumber',
      filter: 'agTextColumnFilter',
      suppressPaste: true,
      suppressFillHandle: true,
    },
    aliasColumn: {
      colId: QuotePartColId.ALIAS,
      headerName: t('headerAlias'),
      filter: 'agTextColumnFilter',
      suppressPaste: true,
      suppressFillHandle: true,
      valueGetter: ({ data }) => BomPartCellHelper.getAlias(data),
      cellRenderer: PartAliasCell,
      minWidth: 150,
      maxWidth: 200,
    },
    statusColumn: {
      colId: QuotePartColId.STATUS,
      headerName: t('headerStatus'),
      field: 'deletedStatus',
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => t(`label${upperFirst(value)}`),
      },
      minWidth: 105,
      width: 105,
      maxWidth: 105,
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      pinned: 'left',
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall noDecorationCell',
      valueGetter: ({ data }) =>
        data
          ? data.omitted
            ? QuotePartStatus.OMITTED
            : data.deleted
            ? QuotePartStatus.DELETED
            : QuotePartStatus.ACTIVE
          : null,
      cellRenderer: StatusCell,
      cellRendererParams: {
        colorMap: {
          [QuotePartStatus.ACTIVE]: Color.SUCCESS,
          [QuotePartStatus.DELETED]: Color.DANGER,
          [QuotePartStatus.OMITTED]: Color.WARNING,
        },
      },
    },
    lineTypeColumn: {
      colId: QuotePartColId.LINE_TYPE,
      headerName: t('headerLineType'),
      sortable: false,
      suppressPaste: true,
      suppressFillHandle: true,
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall noDecorationCell',
      minWidth: 100,
      width: 100,
      maxWidth: 100,
      floatingFilter: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => t(`labelLineType${upperFirst(value)}`),
      },
      valueGetter: ({ data }) => getLineTypeCellValue(data),
      cellRenderer: LineTypeCell,
      cellStyle: { textAlign: 'center' },
    },
    datasheetColumn: {
      colId: QuotePartColId.DATASHEET,
      headerName: t('headerPartDataSheet'),
      suppressPaste: true,
      suppressFillHandle: true,
      resizable: false,
      minWidth: 76,
      width: 76,
      maxWidth: 76,
      cellRenderer: DatasheetCell,
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall',
      cellStyle: { textAlign: 'center' },
      valueGetter: ({ data }) => isEmpty(data?.partArr?.datasheetLink),
    },
  };
};
